import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useState, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import {
  FileText, BarChart3, CreditCard, GitCompare, Award, FolderOpen, Shield,
  LogOut, LogIn, Database,
} from "lucide-react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import PinVerification from "@/components/PinVerification";
import AuthModal from "@/components/AuthModal";
import BankStatements from "@/components/BankStatements";
import CashFlowReport from "@/components/CashFlowReport";
import LoanOffers from "@/components/LoanOffers";
import LoanComparison from "@/components/LoanComparison";
import FinalRecommendation from "@/components/FinalRecommendation";
import SharedView from "@/components/SharedView";
import AnalysisManager from "@/components/AnalysisManager";
import AdminDashboard from "@/components/AdminDashboard";
import AdminCRM from "@/components/AdminCRM";
import NotificationBell from "@/components/NotificationBell";
import MyFiles from "@/components/MyFiles";
import { loadAllDemoData } from "@/lib/api";
import { toast } from "sonner";

function Dashboard() {
  const { user, logout, pinVerified, verifyPin } = useAuth();
  const [activeTab, setActiveTab] = useState("bank-statements");
  const [refreshKey, setRefreshKey] = useState(0);
  const [analysisId, setAnalysisId] = useState("demo");
  const [businessName, setBusinessName] = useState("Acme Business Corp");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const triggerRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const isGuest = !user || user.email === "demo@demo.com";
  const isLoggedIn = user && user.email !== "demo@demo.com";
  const needsPin = isLoggedIn && user.has_pin && !pinVerified;

  const handleAnalysisChange = useCallback((id, analysis) => {
    setAnalysisId(id);
    setBusinessName(analysis?.business_name || "");
    setRefreshKey((k) => k + 1);
  }, []);

  const handleLoadDemo = async () => {
    if (isGuest) {
      setAnalysisId("demo");
      triggerRefresh();
      toast.success("Demo data loaded");
      return;
    }
    try {
      const res = await loadAllDemoData(analysisId);
      if (res.data.analysis_id && res.data.analysis_id !== analysisId) {
        setAnalysisId(res.data.analysis_id);
      }
      toast.success(`Demo loaded: ${res.data.transactions} transactions + ${res.data.offers} loan offers`);
      triggerRefresh();
    } catch (e) {
      toast.error("Failed to load demo data");
    }
  };

  // Require auth for write actions
  const requireAuth = (action) => {
    if (isGuest) {
      setShowAuthModal(true);
      return false;
    }
    return true;
  };

  const handleAuthSuccess = () => {
    triggerRefresh();
    setActiveTab("bank-statements");
  };

  // Show PIN screen if logged in but PIN not verified
  if (needsPin) {
    return <PinVerification onVerified={verifyPin} />;
  }

  const tabs = [
    { id: "bank-statements", label: "Bank Statements", icon: FileText },
    { id: "cash-flow", label: "Cash Flow Report", icon: BarChart3 },
    { id: "loan-offers", label: "Loan Offers", icon: CreditCard },
    { id: "loan-comparison", label: "Comparison", icon: GitCompare },
    { id: "recommendation", label: "Recommendation", icon: Award },
    { id: "my-files", label: "My Files", icon: FolderOpen },
    ...(user?.role === "admin" ? [
      { id: "crm", label: "CRM", icon: Shield },
    ] : []),
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="dashboard">
      {/* Header */}
      <header className="bg-[#1A362D] text-white no-print">
        <div className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-['Playfair_Display'] font-semibold tracking-tight">
              Loan Cash Flow Analyzer
            </h1>
            <p className="text-[#8A9A86] text-sm mt-0.5 font-['IBM_Plex_Sans']">
              {businessName || "Business underwriting & loan comparison tool"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="load-demo-btn"
              variant="ghost"
              size="sm"
              onClick={handleLoadDemo}
              className="text-[#8A9A86] hover:text-white hover:bg-white/10 h-8 gap-1.5 text-xs"
            >
              <Database className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Load Demo</span>
            </Button>
            {isLoggedIn && <AnalysisManager activeId={analysisId} onChange={handleAnalysisChange} />}
            {isLoggedIn && <NotificationBell />}
            {isLoggedIn ? (
              <div className="hidden sm:flex items-center gap-2 text-xs text-[#8A9A86] border-l border-white/20 pl-2 ml-1">
                <span>{user?.name || user?.email}</span>
                <Button
                  data-testid="logout-btn"
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  className="text-[#8A9A86] hover:text-white hover:bg-white/10 h-7 px-2"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <Button
                data-testid="header-login-btn"
                variant="ghost"
                size="sm"
                onClick={() => setShowAuthModal(true)}
                className="text-white hover:bg-white/10 h-8 gap-1.5 text-xs border border-white/20"
              >
                <LogIn className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Sign In</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="no-print bg-white border border-[#E7E5DF] p-1 rounded-md h-auto flex flex-wrap gap-1">
            {tabs.map(({ id, label, icon: Icon }) => (
              <TabsTrigger
                key={id}
                value={id}
                data-testid={`tab-${id}`}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium data-[state=active]:bg-[#1A362D] data-[state=active]:text-white rounded-md transition-colors duration-200"
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="bank-statements">
            <BankStatements refreshKey={refreshKey} onDataChange={triggerRefresh} analysisId={analysisId} requireAuth={requireAuth} />
          </TabsContent>
          <TabsContent value="cash-flow">
            <CashFlowReport refreshKey={refreshKey} analysisId={analysisId} />
          </TabsContent>
          <TabsContent value="loan-offers">
            <LoanOffers refreshKey={refreshKey} onDataChange={triggerRefresh} analysisId={analysisId} requireAuth={requireAuth} />
          </TabsContent>
          <TabsContent value="loan-comparison">
            <LoanComparison refreshKey={refreshKey} analysisId={analysisId} />
          </TabsContent>
          <TabsContent value="recommendation">
            <FinalRecommendation refreshKey={refreshKey} analysisId={analysisId} />
          </TabsContent>
          <TabsContent value="my-files">
            <MyFiles refreshKey={refreshKey} onSwitchAnalysis={(id, a) => { handleAnalysisChange(id, a); setActiveTab("bank-statements"); }} />
          </TabsContent>
          {user?.role === "admin" && (
            <TabsContent value="crm">
              <AdminCRM />
            </TabsContent>
          )}
        </Tabs>
      </main>
      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} onSuccess={handleAuthSuccess} />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/shared/:linkId" element={<SharedView />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </AuthProvider>
  );
}

export default App;
